/**
 * @fileoverview Custom OpenLayers control for switching between map visual effects.
 * @module map/MapEffectControl
 */

import { DOM_ELEMENTS } from '../constants.js';
import { DropdownControl } from './DropdownControl.js';

const { CLASS_ACTIVE_FILTER } = DOM_ELEMENTS;

/**
 * Custom OpenLayers control that provides a dropdown menu for applying CSS filter
 * effects to the map tile layers (e.g., grayscale, sepia, muted).
 * @class
 * @extends DropdownControl
 */
export class MapEffectControl extends DropdownControl {
    /**
     * Creates a new MapEffectControl instance.
     * @param {Object} [options={}] - Control options
     * @param {Array<Object>} [options.effects=[]] - Array of effect objects with id, name, filter properties
     * @param {string} [options.selectedEffect='none'] - ID of the initially selected effect
     * @param {Function} [options.onEffectChange] - Callback invoked when effect changes: (effectId) => void
     * @param {HTMLElement} [options.target] - Target element to render the control into
     */
    constructor(options = {}) {
        super({
            buttonIconHtml: '<i class="bi bi-stars"></i>',
            buttonClass: 'ol-map-effect-button',
            buttonTitle: 'Map visual effects',
            elementClass: 'ol-map-effect',
            dropdownClass: 'map-effect-dropdown',
            target: options.target
        });

        this.effects = options.effects || [];
        this.onEffectChange = options.onEffectChange || (() => {});
        this.currentEffectId = options.selectedEffect || 'none';

        // Populate dropdown with effect options
        this.updateDropdown();

        // Update button state based on initial selection
        this.updateButtonState();
    }

    /**
     * Rebuilds the dropdown menu with current effect options.
     * Highlights the currently active effect.
     * @private
     */
    updateDropdown() {
        this.dropdown.innerHTML = '';

        // Create title bar at the top
        const titleBar = document.createElement('div');
        titleBar.className = 'map-effect-title';
        titleBar.textContent = 'Map Style Effects:';
        this.dropdown.appendChild(titleBar);

        this.effects.forEach((effect) => {
            const item = document.createElement('div');
            item.className = 'map-effect-item';
            item.textContent = effect.name;

            if (effect.id === this.currentEffectId) {
                item.classList.add('active');
            }

            item.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.selectEffect(effect.id);
            });

            this.dropdown.appendChild(item);
        });
    }

    /**
     * Updates the button appearance based on whether a non-default effect is active.
     * @private
     */
    updateButtonState() {
        const button = this.element.querySelector('.ol-map-effect-button');
        if (this.currentEffectId && this.currentEffectId !== 'none') {
            button.classList.add(CLASS_ACTIVE_FILTER);
        } else {
            button.classList.remove(CLASS_ACTIVE_FILTER);
        }
    }

    /**
     * Selects an effect by ID, updates the UI, and fires the onEffectChange callback.
     * @param {string} effectId - ID of the effect to select
     */
    selectEffect(effectId) {
        const effect = this.effects.find(e => e.id === effectId);
        if (effect) {
            this.currentEffectId = effectId;
            this.updateButtonState();
            this.updateDropdown();
            this.hideDropdown();
            this.onEffectChange(effectId);
        }
    }
}
